import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config';

/**
 * The one way a query hook talks to the API: prefixes `API_BASE_URL`, goes
 * through `authService.authenticatedFetch` (auth, refresh, X-Subsidiary,
 * X-Campaign-Id, Content-Type), and turns a non-ok response into the hook's
 * own error. It never notifies `ApiErrorHandler`'s global toast callbacks:
 * hooks own their error UX (expected 404s, inline 409s, background polls).
 */

/** The JSON error body, or `{}` when the body is missing or not JSON. */
export type ApiErrorBody = {
  error?: string;
  message?: string;
  code?: string;
  [key: string]: unknown;
};

/**
 * How a non-ok response becomes the thrown error.
 * - a string: `new Error(string)`; the body is not read.
 * - a function: called with the parsed error body and the response.
 */
/** The row version a 409 `StaleWrite` body names, or null when the body names none (the row is gone, or the code predates it). */
export function staleVersion(body: ApiErrorBody): number | null {
  return typeof body.currentVersion === 'number' ? body.currentVersion : null;
}

export type ApiErrorSpec = string | ((body: ApiErrorBody, response: Response) => Error);

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Sent as `JSON.stringify(json)`. */
  json?: unknown;
  /** Sent untouched (e.g. FormData). Ignored when `json` is set. */
  body?: BodyInit;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface ApiCheckedOptions extends ApiRequestOptions {
  error: ApiErrorSpec;
}

/** Raw response for downloads and status-branching callers; no ok check. */
export function apiResponse(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const init: RequestInit = {};
  if (options.method !== undefined) init.method = options.method;
  if (options.json !== undefined) init.body = JSON.stringify(options.json);
  else if (options.body !== undefined) init.body = options.body;
  if (options.signal !== undefined) init.signal = options.signal;
  if (options.headers !== undefined) init.headers = options.headers;

  const url = `${API_BASE_URL}${path}`;
  return Object.keys(init).length === 0
    ? authService.authenticatedFetch(url)
    : authService.authenticatedFetch(url, init);
}

/** Ok → the parsed JSON body. Every ok status is parsed; use `apiVoid` for 204s. */
export async function apiJson<T>(path: string, options: ApiCheckedOptions): Promise<T> {
  const response = await checked(path, options);
  return response.json() as Promise<T>;
}

/** Ok (including 204) → undefined; the body is never read. */
export async function apiVoid(path: string, options: ApiCheckedOptions): Promise<void> {
  await checked(path, options);
}

async function checked(path: string, { error, ...request }: ApiCheckedOptions): Promise<Response> {
  const response = await apiResponse(path, request);
  if (response.ok) return response;
  if (typeof error === 'string') throw new Error(error);
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw error(body, response);
}
